import { Box, CircularProgress } from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FONT_MONO } from "../../theme/v2Tokens";
import {
  Transcription,
  TranscriptionReader,
  TranscriptionBlock,
} from "../../../types/transcription/Transcription";
import { useTranscriptRequest } from "../../../hooks/useTranscription";
import { useTranscribeAndSummarize } from "../../../hooks/useSummary";
import { archieButtonSx, archieIconSvg } from "./SummaryTab";
import { AuthContext } from "../../../context/AuthContext";

interface TranscriptTabProps {
  customerId: string;
  interactionId: string;
  interactionType: string;
  hasMedia: boolean;
  transcript?: Transcription;
  isLoading: boolean;
  currentTimeSeconds?: number;
}

function formatBlockTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function TranscriptTab({
  customerId,
  interactionId,
  interactionType,
  hasMedia,
  transcript,
  isLoading,
  currentTimeSeconds = 0,
}: TranscriptTabProps) {
  const { t } = useTranslation(["common", "summary"]);
  const { user } = useContext(AuthContext);
  const canSummarize = user?.hasPermission("summary-templates:Read");

  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const transcriptRequest = useTranscriptRequest();
  const transcribeAndSummarize = useTranscribeAndSummarize();

  const blocks: TranscriptionBlock[] = useMemo(() => {
    if (!transcript) return [];
    try {
      const reader = new TranscriptionReader(transcript);
      return reader.blocks;
    } catch {
      return [];
    }
  }, [transcript]);

  const canTranscribe =
    ["Audio", "Video", "AudioAndVideo"].includes(interactionType) && hasMedia;

  const activeBlockId = useMemo(() => {
    if (!blocks.length || currentTimeSeconds <= 0) return -1;
    for (let i = blocks.length - 1; i >= 0; i--) {
      if (blocks[i].startSeconds <= currentTimeSeconds) {
        return blocks[i].id;
      }
    }
    return -1;
  }, [blocks, currentTimeSeconds]);

  const activeBlockRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeBlockRef.current) {
      activeBlockRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeBlockId]);

  const handleTranscribe = () => {
    setRequestError(null);
    setRequestSent(true);
    transcriptRequest.mutate(
      { customerId, interactionId },
      {
        onSuccess: () => setRequestSent(false),
        onError: (err) => {
          setRequestSent(false);
          setRequestError(err.message || t("redactionRequestFailed"));
        },
      },
    );
  };

  const handleTranscribeAndSummarize = () => {
    setRequestError(null);
    setRequestSent(true);
    transcribeAndSummarize.mutate(
      { customerId, interactionId },
      {
        onSuccess: () => setRequestSent(false),
        onError: (err) => {
          setRequestSent(false);
          setRequestError(err.message || t("redactionRequestFailed"));
        },
      },
    );
  };

  const isPending =
    requestSent ||
    transcriptRequest.isPending ||
    transcribeAndSummarize.isPending;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 120,
          gap: 1,
        }}
      >
        <CircularProgress size={16} />
        <Box
          component="span"
          sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
        >
          {t("loadingTranscript")}
        </Box>
      </Box>
    );
  }

  if (blocks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 4,
          px: 2,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: "var(--text-tertiary)",
            mb: 1,
          }}
        >
          {t("noTranscriptAvailable")}
        </Box>

        {requestError && (
          <Box sx={{ fontSize: "0.8125rem", color: "var(--signal-rose)" }}>
            {requestError}
          </Box>
        )}

        {canTranscribe && !isPending && (
          <>
            <Box
              component="button"
              onClick={handleTranscribe}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: "var(--accent)",
                border: "none",
                borderRadius: "8px",
                px: 2.5,
                py: 1,
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { opacity: 0.88 },
              }}
            >
              Transcribe
            </Box>
            <Box
              component="button"
              onClick={canSummarize ? handleTranscribeAndSummarize : undefined}
              disabled={!canSummarize}
              title={!canSummarize ? t("summary:noPermission") : undefined}
              sx={{
                ...archieButtonSx,
                ...(!canSummarize && {
                  opacity: 0.5,
                  cursor: "not-allowed",
                  "&:hover": { opacity: 0.5 },
                }),
              }}
            >
              {archieIconSvg}
              Transcribe & Summarize with Archie
            </Box>
          </>
        )}

        {isPending && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} />
            <Box
              component="span"
              sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}
            >
              Transcription requested...
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {requestError && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid var(--border-subtle)",
            fontSize: "0.8125rem",
            color: "var(--signal-rose)",
            flexShrink: 0,
          }}
        >
          {requestError}
        </Box>
      )}

      <Box sx={{ py: 1 }}>
        {blocks.map((block) => {
          const isActive = block.id === activeBlockId;
          return (
            <Box
              key={block.id}
              ref={isActive ? activeBlockRef : undefined}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                px: 2,
                py: 0.875,
                borderBottom: "1px solid var(--border-subtle)",
                "&:last-child": { borderBottom: "none" },
                backgroundColor: isActive ? "var(--accent-dim)" : "transparent",
                borderRadius: isActive ? "4px" : 0,
                transition: "background-color 200ms ease",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: "0.6875rem",
                  color: isActive ? "var(--accent)" : "var(--text-tertiary)",
                  flexShrink: 0,
                  minWidth: 32,
                  mt: "2px",
                }}
              >
                {formatBlockTime(block.startSeconds)}
              </Box>

              <Box
                component="span"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color:
                    block.speakerLabel === "Agent"
                      ? "var(--signal-blue)"
                      : "var(--signal-amber)",
                  flexShrink: 0,
                  minWidth: 60,
                  mt: "2px",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {block.speakerLabel}
              </Box>

              <Box
                component="span"
                sx={{
                  fontSize: "0.8125rem",
                  color: isActive
                    ? "var(--text-primary)"
                    : "var(--text-secondary)",
                  lineHeight: 1.5,
                  fontWeight: isActive ? 500 : 400,
                }}
              >
                {block.text}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
